import { useAddLeadNoteMutation } from "@/app/leads/leads.api";
import { useFormik } from "formik";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { debounce } from "lodash";

interface NotesTabsProps {
  note: string;
  leadId: string | number;
}

const NotesTabs = ({ note, leadId }: NotesTabsProps) => {
  const [addNote, { isLoading: isLoadingNote }] = useAddLeadNoteMutation();
  const [newNote, setNewNote] = useState({ title: "", description: note || "" });

  const formikNotes = useFormik({
    initialValues: {
      title: "",
      description: note || "",
    },
    onSubmit: async (values) => {
      try {
        // Combine title and description into a single string
        const newNoteString = values.title
          ? values.description
            ? `${values.title}: ${values.description}`
            : values.title
          : values.description || "";
        // Append new note to existing note with newline
        const notesString = note ? `${note}\n${newNoteString}` : newNoteString;
        if (newNoteString) {
          // Only submit if there's new content to avoid validation error
          await addNote({
            leadId,
            notes: notesString,
          }).unwrap();
          // Update local state with the new input instead of resetting
          setNewNote({ title: values.title, description: values.description });
        }
      } catch (error) {
        console.error("Failed to add note:", error);
        // Handle error (e.g., show toast notification)
      }
    },
  });

  // Sync form with local state when note prop changes (initial load or refetch)
  useEffect(() => {
    formikNotes.setValues({
      title: newNote.title,
      description: newNote.description || note || "",
    });
  }, [note]);

  // Debounced submission to avoid excessive API calls
  const debouncedSubmit = debounce(() => {
    if (formikNotes.isValid && formikNotes.dirty) {
      formikNotes.submitForm();
    }
  }, 500); // 500ms debounce delay

  // Trigger submission on form value changes
  useEffect(() => {
    debouncedSubmit();
    // Cleanup debounce on unmount
    return () => {
      debouncedSubmit.cancel();
    };
  }, [formikNotes.values.title, formikNotes.values.description]);

  return (
    <div>
      <div className="w-full">
        <h1 className="text-base font-semibold leading-6 text-gray-900 border-b border-gray-200 pb-3 mt-4 mb-5 md:mb-3">
          {t("user.views.notes")}
        </h1>
        <form onSubmit={formikNotes.handleSubmit} className="relative mt-4">
          <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
            <label htmlFor="title" className="sr-only">
              {t("lead.views.tittle")}
            </label>
            <input
              id="title"
              name="title"
              type="text"
              placeholder={t("lead.views.tittle")}
              className="block w-full border-0 pt-2.5 text-lg font-medium placeholder:text-gray-400 focus:ring-0"
              value={formikNotes.values.title}
              onChange={formikNotes.handleChange}
              onBlur={formikNotes.handleBlur}
            />
            {formikNotes.touched.title && formikNotes.errors.title && (
              <div className="text-red-600 text-sm mt-1">{formikNotes.errors.title}</div>
            )}

            <label htmlFor="description" className="sr-only">
              {t("lead.views.description")}
            </label>
            <textarea
              id="description"
              name="description"
              rows={8}
              placeholder={t("lead.views.write_description")}
              className="block w-full resize-none border-0 py-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              value={formikNotes.values.description}
              onChange={formikNotes.handleChange}
              onBlur={formikNotes.handleBlur}
            />
            {formikNotes.touched.description && formikNotes.errors.description && (
              <div className="text-red-600 text-sm mt-1">{formikNotes.errors.description}</div>
            )}

            <div aria-hidden="true">
              <div className="py-3">
                <div className="h-3" />
              </div>
              <div className="h-px" />
              <div className="py-3">
                <div className="py-px">
                  <div className="h-3" />
                </div>
              </div>
            </div>
          </div>
          {isLoadingNote && (
            <div className="mt-2 text-sm text-gray-500">{t("common.saving")}</div>
          )}
        </form>
      </div>
    </div>
  );
};

export default NotesTabs;